import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import HospitalizationPlan from 'model/HospitalizationPlan'
import HospitalizationPlansRepository from 'clients/db/HospitalizationPlansRepository'
import { AppThunk } from 'store'

interface HospitalizationState {
  isLoading: boolean
  /** Admissions recorded for the patient currently in view, newest first. */
  plans: HospitalizationPlan[]
  /** Every admission currently running, across patients — powers the dashboard panel. */
  activePlans: HospitalizationPlan[]
  isLoadingActive: boolean
}

const initialState: HospitalizationState = {
  isLoading: false,
  plans: [],
  activePlans: [],
  isLoadingActive: false,
}

const hospitalizationSlice = createSlice({
  name: 'hospitalization',
  initialState,
  reducers: {
    fetchStart(state) {
      state.isLoading = true
    },
    fetchSuccess(state, { payload }: PayloadAction<HospitalizationPlan[]>) {
      state.plans = payload
      state.isLoading = false
    },
    fetchFailed(state) {
      state.isLoading = false
    },
    clearPlans(state) {
      state.plans = []
    },
    fetchActiveStart(state) {
      state.isLoadingActive = true
    },
    fetchActiveSuccess(state, { payload }: PayloadAction<HospitalizationPlan[]>) {
      state.activePlans = payload
      state.isLoadingActive = false
    },
    fetchActiveFailed(state) {
      state.isLoadingActive = false
    },
    upsertPlan(state, { payload }: PayloadAction<HospitalizationPlan>) {
      const index = state.plans.findIndex(plan => plan.id === payload.id)
      state.plans =
        index >= 0
          ? [...state.plans.slice(0, index), payload, ...state.plans.slice(index + 1)]
          : [payload, ...state.plans]
    },
  },
})

export const {
  fetchStart,
  fetchSuccess,
  fetchFailed,
  clearPlans,
  upsertPlan,
  fetchActiveStart,
  fetchActiveSuccess,
  fetchActiveFailed,
} = hospitalizationSlice.actions

/** Every running admission across patients, for the hospitalization dashboard. */
export const fetchActiveHospitalizations = (): AppThunk => async dispatch => {
  dispatch(fetchActiveStart())
  try {
    const plans = await HospitalizationPlansRepository.findAllOngoing()
    dispatch(fetchActiveSuccess(plans))
  } catch (error) {
    console.error(error)
    dispatch(fetchActiveFailed())
  }
}

export const fetchHospitalizationPlans = (patientId: string): AppThunk => async dispatch => {
  if (!patientId) return
  dispatch(fetchStart())
  try {
    const plans = await HospitalizationPlansRepository.searchByPatient(patientId)
    dispatch(fetchSuccess(plans))
  } catch (error) {
    console.error(error)
    dispatch(fetchFailed())
  }
}

/** Persist a new or updated admission record and reflect it in the store. */
export const saveHospitalizationPlan = (
  plan: HospitalizationPlan,
  onSaved?: (saved: HospitalizationPlan) => void,
): AppThunk => async dispatch => {
  const saved = await HospitalizationPlansRepository.saveOrUpdate(plan)
  dispatch(upsertPlan(saved))
  onSaved && onSaved(saved)
}

export default hospitalizationSlice.reducer
